import React, { useCallback, useEffect, useRef, useState } from 'react';
import Icon from 'components/Icon';
import TranslationControlsButton from 'components/common/TranslationControlsButton';
import AudioControlsButton from 'components/common/AudioControlsButton';
import classnames from 'classnames';
import styles from './index.module.scss';
import { useStore, StoreTypes } from 'context';
import Repository from 'repositories/Repository';
import useSetState from 'customHooks/setState';
import useScroll from 'customHooks/scroll';
import { findLastIndex } from 'util/array';
import { ReaderToolsEvent } from 'events/EventTypes';
import { EventBusType, EventBus } from 'events/EventBus';
import { CanvasEvent } from 'events/EventTypes';

const eventBusType = EventBusType.ExtendedContent;




const { ExtendedResourceRepository } = Repository;

const PageControlDots = ({ data, onClick, scrollOffset, offsetList }) => {
  const [{ activeIndex }, setState] = useSetState({
    activeIndex: 0
  });

  useEffect(() => {
    const BUFFER = 40;
    const lastIndex = findLastIndex(offsetList, item => {
      if (item) {
        return scrollOffset + item.height + BUFFER >= item.offsetTop;
      }
    });

    setState({ activeIndex: lastIndex });
  }, [offsetList, scrollOffset, setState]);

  const clickHandler = activeIndex => {
    setState({ activeIndex });
    onClick(activeIndex);
  };

  return (
    <>
      {data.map(item => (
        <div
          key={item}
          className={classnames(styles.pageControlDot, {
            [styles.active]: activeIndex === item
          })}
          onClick={() => clickHandler(item)}
        ></div>
      ))}
    </>
  );
};

const Translation = ({ data: { contents, parts = [] } }) => {
  const [{ shouldSplited }] = useStore(StoreTypes.extendedContent);

  return (shouldSplited ? <TranslationWithSplited data={{contents, parts}} /> : <TranslationWithoutSplited data={{contents, parts}} />)
 
};


const TranslationWithoutSplited = ({ data: { contents, parts = [] } }) => {
  const [{ bookId }] = useStore(StoreTypes.books);
  const [{ fontSize }] = useStore(
    StoreTypes.extendedContent
  );
  const [{ partialInfo }] = useStore(StoreTypes.extendedContent);
  const {
    scrollTop
  } = partialInfo;

  const listOffestTopRef = useRef([]);
  const scrollRef = useRef();
  const { y } = useScroll(scrollRef);
  const scrollTo = (id, opt = { behavior: 'smooth' }) => {
    document.querySelector(`#${id}`).scrollIntoView({
      behavior: opt.behavior
    });
  };
  
  const initViewInfo = ()=> {
    const viewHeight = scrollRef.current.offsetHeight;
    const scrollHeight = scrollRef.current.scrollHeight;
    const scrollTop = scrollRef.current.scrollTop;
    const space = viewHeight / 3 * 2;
    EventBus.emit({
      eventBusType,
      event: ReaderToolsEvent.SetExtendedContentPartialInfo,
      payload: {
        partialInfo: {
          viewHeight,
          scrollHeight,
          scrollTop,
          space
        }
      }
    });
  }

  useEffect(()=>{
    EventBus.emit({
      eventBusType,
      event: ReaderToolsEvent.SetExtendedContentPartialInfo,
      payload: {
        partialInfo: {
          ...partialInfo,
          scrollTop:y
        }
      }
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[y,fontSize])

  useEffect(()=>{
    scrollRef.current.scrollTo({top: scrollTop, behavior: 'smooth'});
  },[scrollTop])

  useEffect(()=>{
    initViewInfo();
  },[fontSize])

  const [{ translationData, isEnShow, isChShow }, setState] = useSetState({
    translationData: contents,
    isEnShow: true,
    isChShow: false,
    isAllShow: false
  });

  const toggleEnShow = useCallback(() => {
    const data = translationData.map(item => ({
      ...item,
      enStatus: !isEnShow
    }));
    setState({ isEnShow: !isEnShow, translationData: data });
  }, [isEnShow, setState, translationData]);

  const toggleChShow = useCallback(() => {
    const data = translationData.map(item => ({
      ...item,
      chStatus: !isChShow
    }));
    setState({ isChShow: !isChShow, translationData: data });
  }, [isChShow, setState, translationData]);

  const setAllLangShow = useCallback(() => {
    const status = isEnShow && isChShow ? false : true;
    const data = translationData.map(item => ({
      ...item,
      enStatus: status,
      chStatus: status
    }));
    setState({ translationData: data, isEnShow: status, isChShow: status });
  }, [isChShow, isEnShow, setState, translationData]);

  const toggleOne = useCallback(
    (key, index) => {
      let temp = translationData;
      temp[index][key] = !temp[index][key];
      const isAllEnShow = temp.every(item => item.enStatus);
      const isAllChShow = temp.every(item => item.chStatus);
      setState({
        translationData: temp,
        isEnShow: isAllEnShow,
        isChShow: isAllChShow
      });
    },
    [setState, translationData]
  );

  useEffect(() => {
    // setup default data
    setState({
      translationData: contents.map(item => ({
        ...item,
        enStatus: true,
        chStatus: false
      }))
    });
  }, [contents, setState]);

  const pageControlDotsClickHandler = index => {
    scrollTo(`part${index}`);
  };

  const setOffsetTop = useCallback(
    (el, index) => {
      if (parts.includes(index)) {
        listOffestTopRef.current[index] = {
          offsetTop: el.offsetTop,
          height: el.offsetHeight
        };
      }
    },
    [parts]
  );


  return (
    <div className={styles.translationWrapper}>
      {
        parts && parts.length > 0 && <div className={styles.pageControl}>
          <PageControlDots
            offsetList={listOffestTopRef.current}
            data={parts}
            onClick={pageControlDotsClickHandler}
            scrollOffset={y}
          />
        </div>
      }
      <div className={classnames(styles.noSplited,styles.controls)}>
        <TranslationControlsButton
          content="英文"
          active={isEnShow}
          onClick={() => toggleEnShow()}
        />
        <TranslationControlsButton
          content="中文"
          active={isChShow}
          onClick={() => toggleChShow()}
        />
        <TranslationControlsButton
          content="全部"
          active={isEnShow && isChShow}
          onClick={() => setAllLangShow()}
        />
      </div>
      <div ref={scrollRef} className={classnames(styles.translation, styles[fontSize])}>
        <div className={styles.contents}>
          {translationData.map((item, index) => (
            <div
              id={`part${index}`}
              className={styles.contentList}
              key={item.en}
              ref={el => (el ? setOffsetTop(el, index) : null)}
            >
              <div className={classnames(styles.listItem, styles.en)}>
                <div className={styles.icon}>
                  <AudioControlsButton src={ExtendedResourceRepository.getDataSrc({
                    bookId,
                    pathName: item.mp3
                  })} />
                </div>
                <div
                  className={classnames(styles.sentence, {
                    [styles.active]: item.enStatus
                  })}
                  onClick={() => toggleOne('enStatus', index)}
                >
                  {Array.isArray(item.en) ? (
                    item.en.map(text => (
                      <div
                        key={text}
                        dangerouslySetInnerHTML={{ __html: text }}
                      />))
                  ) : (
                      <div
                        dangerouslySetInnerHTML={{ __html: item.en }}
                      />
                    )}
                </div>
              </div>
              <div className={classnames(styles.listItem, styles.ch)}>
                <div className={styles.icon}>
                  <Icon type="text" name="longArrowAltRight" />
                </div>
                <div
                  className={classnames(styles.sentence, {
                    [styles.active]: item.chStatus
                  })}
                  onClick={() => toggleOne('chStatus', index)}
                >
                  {Array.isArray(item.ch) ? (
                    item.ch.map(text => <div>{text}</div>)
                  ) : (
                      <div>{item.ch}</div>
                    )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const TranslationWithSplited = ({ data: { contents, parts = [] } }) => {
  const [{ bookId }] = useStore(StoreTypes.books);
  const [{ fontSize }] = useStore(
    StoreTypes.extendedContent
  );
  // const [{ partialInfo }] = useStore(StoreTypes.extendedContent);
  // const {
  //   scrollTop
  // } = partialInfo;

  const scrollRef = useRef();
  const contentRef = useRef();

  const [{ translationData, isEnShow, isChShow, currentPageIndex }, setState] = useSetState({
    translationData: contents,
    isEnShow: true,
    isChShow: false,
    isAllShow: false,
    currentPageIndex: 0
  });

  // 獲取所有頁面的 key
  const pageKeys = Object.keys(translationData);
  const currentPageKey = pageKeys[currentPageIndex];

  const toggleEnShow = useCallback(() => {
    const data = { ...translationData };
    Object.keys(data).forEach(pageKey => {
      data[pageKey] = data[pageKey].map(item => ({
        ...item,
        enStatus: !isEnShow
      }));
    });
    setState({ isEnShow: !isEnShow, translationData: data });
  }, [isEnShow, setState, translationData]);

  const toggleChShow = useCallback(() => {
    const data = { ...translationData };
    Object.keys(data).forEach(pageKey => {
      data[pageKey] = data[pageKey].map(item => ({
        ...item,
        chStatus: !isChShow
      }));
    });
    setState({ isChShow: !isChShow, translationData: data });
  }, [isChShow, setState, translationData]);

  const setAllLangShow = useCallback(() => {
    const status = isEnShow && isChShow ? false : true;
    const data = { ...translationData };
    Object.keys(data).forEach(pageKey => {
      data[pageKey] = data[pageKey].map(item => ({
        ...item,
        enStatus: status,
        chStatus: status
      }));
    });
    setState({ translationData: data, isEnShow: status, isChShow: status });
  }, [isChShow, isEnShow, setState, translationData]);

  const toggleOne = useCallback(
    (key, itemIndex) => {
      const temp = { ...translationData };
      temp[currentPageKey][itemIndex][key] = !temp[currentPageKey][itemIndex][key];
      
      // 檢查所有頁面的所有項目是否都啟用了語言顯示
      const allItems = Object.values(temp).flat();
      const isAllEnShow = allItems.every(item => item.enStatus);
      const isAllChShow = allItems.every(item => item.chStatus);
      
      setState({
        translationData: temp,
        isEnShow: isAllEnShow,
        isChShow: isAllChShow
      });
    },
    [setState, translationData, currentPageKey]
  );

  // 分頁控制函數
  const goToPage = useCallback((pageIndex) => {
    setState({ currentPageIndex: pageIndex });
    EventBus.emit({
      eventBusType: EventBusType.ExtendedContent,
      event: CanvasEvent.SplitedPageSaveCanvasEvent,
      payload: { currentSplitedPageIndex: currentPageIndex }
    });
    }, [setState, currentPageIndex]);

  useEffect(() => {
    EventBus.emit({
      eventBusType: EventBusType.ExtendedContent,
      event: CanvasEvent.SplitedPageImportJSONEvent,
      payload: { currentSplitedPageIndex: currentPageIndex }
    });
  }, [currentPageIndex]);

  useEffect(() => {
    if (Array.isArray(contents)) return;
    // setup default data - 將物件格式的 contents 轉換為帶有 enStatus 和 chStatus 的格式
    const initialData = {};
    Object.keys(contents).forEach(pageKey => {
      initialData[pageKey] = contents[pageKey].map(item => ({
        ...item,
        enStatus: true,
        chStatus: false
      }));
    });
    
    setState({
      translationData: initialData,
      currentPageIndex: 0
    });
  }, [contents, setState]);

  // const pageControlDotsClickHandler = index => {
  //   goToPage(index);
  // };

  // const setOffsetTop = useCallback(
  //   (el, index) => {
  //     if (parts.includes(index)) {
  //       listOffestTopRef.current[index] = {
  //         offsetTop: el.offsetTop,
  //         height: el.offsetHeight
  //       };
  //     }
  //   },
  //   [parts]
  // );

  // 獲取當前頁面的數據
  const currentPageItems = translationData[currentPageKey] || [];

  // 高效縮放策略 - 避免動態迭代，使用一次性數學計算
  const adjustContentScale = useCallback(() => {
    if (!scrollRef.current || !contentRef.current) return;

    const container = scrollRef.current;
    const content = contentRef.current;

    // 重置所有樣式，確保測量準確
    content.style.fontSize = '';
    content.style.transform = 'none';
    content.style.zoom = '';

    // 批次 DOM 操作，減少重排次數
    void container.offsetHeight; // 強制重排一次

    // 一次性計算所需的縮放比例
    const containerHeight = container.clientHeight - 80;
    const contentHeight = content.scrollHeight;
    const scaleRatio = Math.min(containerHeight / contentHeight, 1);

    if (scaleRatio < 1) {
      // 根據瀏覽器支援選擇最佳方法
      if ('zoom' in document.documentElement.style) {
        // 現代瀏覽器：優先使用 zoom（單次操作，效能更好）
        content.style.zoom = scaleRatio;
      } else {
        // 後備方案：使用 transform scale
        content.style.transform = `scaleY(${scaleRatio})`;
        content.style.transformOrigin = 'top left';
      }
    }
  }, []);

  // 當頁面切換、字體大小改變或語言顯示狀態改變時調整縮放
  useEffect(() => {
     requestAnimationFrame(()=>{adjustContentScale()});
  }, [currentPageIndex, isEnShow, isChShow, adjustContentScale]);

  return (
    <div className={styles.translationWrapper}>
      {/* {
        parts && parts.length > 0 && <div className={styles.pageControl}>
          <PageControlDots
            offsetList={listOffestTopRef.current}
            data={parts}
            onClick={pageControlDotsClickHandler}
            scrollOffset={y}
          />
        </div>
      } */}
      <div className={classnames(styles.splited,styles.controls)}>
      <div className={styles.paginationControls}>
          {pageKeys.map((pageKey, index) => (
            <div
              key={pageKey}
              className={classnames(styles.pageButton, {
                [styles.active]: currentPageIndex === index,
              })}
              onClick={() => goToPage(index)}
            >
              {pageKey}
            </div>
          ))}
        </div>
        <div className={styles.translationControls}>
          <TranslationControlsButton
            content="英文"
            active={isEnShow}
            onClick={() => toggleEnShow()}
          />
          <TranslationControlsButton
            content="中文"
            active={isChShow}
            onClick={() => toggleChShow()}
          />
          <TranslationControlsButton
            content="全部"
            active={isEnShow && isChShow}
            onClick={() => setAllLangShow()}
          />
        </div>
      </div>
      <div
        ref={scrollRef}
        className={classnames(styles.translation, styles.splited, styles[fontSize])}
      >
        <div ref={contentRef} className={styles.contents}>
          {Array.isArray(currentPageItems) && currentPageItems.map((item, itemIndex) => (
            <div
              id={`part${currentPageKey}_${itemIndex}`}
              className={styles.contentList}
              key={`${currentPageKey}_${itemIndex}`}
              // ref={el => (el ? setOffsetTop(el, currentPageIndex) : null)}
            >
              <div className={classnames(styles.listItem, styles.en)}>
                <div className={styles.icon}>
                  <AudioControlsButton
                    src={ExtendedResourceRepository.getDataSrc({
                      bookId,
                      pathName: item.mp3,
                    })}
                  />
                </div>
                <div
                  className={classnames(styles.sentence, {
                    [styles.active]: item.enStatus,
                  })}
                  onClick={() => toggleOne("enStatus", itemIndex)}
                >
                  {Array.isArray(item.en) ? (
                    item.en.map((text) => (
                      <div
                        key={text}
                        dangerouslySetInnerHTML={{ __html: text }}
                      />
                    ))
                  ) : (
                    <div dangerouslySetInnerHTML={{ __html: item.en }} />
                  )}
                </div>
              </div>
              <div className={classnames(styles.listItem, styles.ch)}>
                <div className={styles.icon} onClick={() => toggleOne("chStatus", itemIndex)}>
                  <Icon type="text" name="longArrowAltRight" />
                </div>
                <div
                  className={classnames(styles.splited, styles.sentence, {
                    [styles.active]: item.chStatus,
                  })}
                  onClick={() => toggleOne("chStatus", itemIndex)}
                >
                  {Array.isArray(item.ch) ? (
                    item.ch.map((text) => <div key={text}>{text}</div>)
                  ) : (
                    <div>{item.ch}</div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Translation;
